import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useBooking } from "./BookingContext";
import { MaskLine, LuxButton } from "./primitives";
import { IMAGES } from "./content";

const EASE = [0.16, 1, 0.3, 1];

export default function Hero() {
  const { openBooking } = useBooking();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 220]);
  const scale = useTransform(scrollY, [0, 900], [1, 1.15]);
  const overlayOpacity = useTransform(scrollY, [0, 700], [0.18, 0.5]);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative h-[100svh] w-full overflow-hidden bg-charcoal grain"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <video
          className="h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={IMAGES.heroPoster}
        >
          <source src={IMAGES.heroVideoWebm} type="video/webm" />
          <source src={IMAGES.heroVideo} type="video/mp4" />
        </video>
      </motion.div>

      {/* Light scrim — keeps the video bright & refreshing */}
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-charcoal" />
      {/* Bottom + top scrims for legibility; center stays bright & refreshing */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-charcoal/85 via-charcoal/10 to-charcoal/45" />

      <div
        className="relative z-10 mx-auto flex h-full max-w-[1600px] flex-col justify-end px-6 pb-24 md:px-12 md:pb-28"
        style={{ textShadow: "0 2px 30px rgba(17,17,17,0.55)" }}
      >
        <motion.span
          className="overline mb-8 text-warm text-sm md:text-base"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
        >
          The Luxury Legacy Villa Formula
        </motion.span>

        <h1 className="max-w-[18ch] font-serif text-[2.4rem] font-light leading-[1.04] text-warm sm:text-5xl lg:text-[4.4rem]">
          <MaskLine trigger="load" delay={0.3}>Create a luxury asset</MaskLine>
          <MaskLine trigger="load" delay={0.45}>that carries your legacy,</MaskLine>
          <MaskLine trigger="load" delay={0.6} className="italic text-champagne">
            elevates your lifestyle,
          </MaskLine>
          <MaskLine trigger="load" delay={0.75}>and delivers strong returns.</MaskLine>
        </h1>

        <motion.p
          className="mt-8 max-w-[52ch] text-base leading-relaxed text-warm/70 md:text-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 1, ease: EASE }}
        >
          Most second-home owners believe they must choose between timeless design,
          unforgettable experiences, and exceptional investment performance. We help them
          achieve all three.
        </motion.p>

        <motion.div
          className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 1, ease: EASE }}
        >
          <LuxButton testid="hero-primary-cta" variant="light" onClick={openBooking}>
            Book Your Villa Design Strategy Call
          </LuxButton>
        </motion.div>
      </div>

      <motion.div
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-warm/50"
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 1, 0], y: [0, 8, 8, 0] }}
        transition={{ delay: 2, duration: 2.4, repeat: Infinity }}
      >
        <span className="overline text-[0.6rem]">Scroll</span>
      </motion.div>
    </section>
  );
}
